import { faGrid, faTable } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inMarketTheme } from '@inmarket/inmarkit';
import { Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';

import useConfirm from '../../hooks/useConfirm';
import { useReportingHubPageContext } from '../../pages/ReportingHubPageContext';
import { ReportTemplate } from '../../validationSchemas/ReportTemplateSchema';
import ReportTemplateListByUser from './ReportTemplateListByUsers';
import ReportTemplateTableByUser from './ReportTemplateTableByUsers';

const customStyle1 = {
  fontSize: '12px',
  color: '#616161',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '1.68px',
};

interface User {
  id: number;
  name: string;
}
interface ReportTemplateListProps {
  userList: User[];
  duplicateReportTemplateList: (selectedTemplates: ReportTemplate[], confirmMessage: () => Promise<unknown>) => void;
  deleteReportTemplateList: (selectedTemplates: ReportTemplate[], confirmMessage: () => Promise<unknown>) => void;
}

function ReportTemplateList({
  userList,
  duplicateReportTemplateList,
  deleteReportTemplateList,
}: ReportTemplateListProps) {
  const [display, setDisplay] = useState('grid');
  const { reportTemplates: reportTemplatesRaw } = useReportingHubPageContext();

  const reportTemplates: ReportTemplate[] = reportTemplatesRaw?.map((item) => item.template?.reportTemplate);

  const [selectedReportTemplateIds, setSelectedReportTemplateIds] = useState<GridRowSelectionModel>([]);
  const [selectedReportTemplates, setSelectedReportTemplates] = useState<ReportTemplate[]>([]);
  useEffect(() => {
    setSelectedReportTemplates(
      selectedReportTemplateIds.map((selectedReportTemplateId) =>
        reportTemplates?.find(
          (reportTemplate: ReportTemplate) => reportTemplate.templateId === selectedReportTemplateId
        )
      )
    );
  }, [selectedReportTemplateIds, reportTemplates]);

  const { windowDotConfirm: windowDotConfirmDuplicate } = useConfirm(
    `Are you sure you want to duplicate ${selectedReportTemplates
      .map((reportTemplate: ReportTemplate) => `"${reportTemplate?.reportName}"`)
      .join(', ')}?`
  );

  const { windowDotConfirm: windowDotConfirmDelete } = useConfirm(
    `Are you sure you want to delete ${selectedReportTemplates
      .map((reportTemplate: ReportTemplate) => `"${reportTemplate?.reportName}"`)
      .join(', ')}?`
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row">
        <Stack flex={1}>
          <Typography sx={customStyle1}>Saved Templates</Typography>
        </Stack>
        <Stack flex={2} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={display} onChange={(_, newValue) => setDisplay(newValue)}>
            <ToggleButton value="grid">
              <FontAwesomeIcon icon={faGrid} color={display === 'grid' && inMarketTheme.palette.primary.main} />
            </ToggleButton>
            <ToggleButton value="table">
              <FontAwesomeIcon icon={faTable} color={display === 'table' && inMarketTheme.palette.primary.main} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack flex={1} direction="row" spacing={1} justifyContent="end" width="100%">
          <Button
            variant="outlined"
            disabled={selectedReportTemplateIds?.length === 0}
            onClick={() => duplicateReportTemplateList(selectedReportTemplates, windowDotConfirmDuplicate)}
          >
            Clone
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={selectedReportTemplateIds?.length === 0}
            onClick={() => deleteReportTemplateList(selectedReportTemplates, windowDotConfirmDelete)}
          >
            Delete
          </Button>
        </Stack>
      </Stack>
      {display === 'grid' && (
        <ReportTemplateListByUser
          userList={userList}
          selectedReportTemplateIds={selectedReportTemplateIds}
          setSelectedReportTemplateIds={setSelectedReportTemplateIds}
        />
      )}
      {display === 'table' && (
        <ReportTemplateTableByUser
          userList={userList}
          selectedReportTemplateIds={selectedReportTemplateIds}
          setSelectedReportTemplateIds={setSelectedReportTemplateIds}
        />
      )}
    </Stack>
  );
}

export default ReportTemplateList;
